import React, { useState, useEffect, useRef } from 'react';
import { API_BASE } from '../config/apiConfig';

/**
 * Default CID JSON structure used as fallback when live CID data is unavailable.
 * Returns a product-type-aware neutral fallback — NOT hardcoded to Restaurant.
 * Pass the subscriber's product_type to get a correctly labelled placeholder.
 */
export const buildDefaultCidJson = (productType = '', location = '') => {
  // Derive a human-readable context from product_type prefix
  const prefix = productType.includes('.') ? productType.split('.')[0] : productType;
  const CONTEXT_MAP = {
    'Restaurant': 'Restaurant Operations',
    'Gym': 'Gym & Fitness Operations',
    'IndustrialWater-PFAS-Intelligence': 'Industrial Water & PFAS Compliance Intelligence',
    'IndustrialWater': 'Industrial Water Operations',
    'BioPharma-Executive-Intelligence': 'BioPharma Executive Intelligence',
    'BioPharma': 'BioPharma Operations',
    'China-US-Strategic-Intel': 'China-US Geopolitical Intelligence',
    'Procurement': 'Procurement Operations',
    'Financial-Bank': 'Financial Banking Operations',
    'Insurance': 'Insurance Operations',
    'Healthcare': 'Healthcare Operations',
    'Logistics': 'Logistics Operations',
    'Supermarket': 'Supermarket Operations',
    'Grocery': 'Supermarket Operations',
    'Realty': 'Real Estate Operations',
    'RealEstate': 'Real Estate Operations',
    'CarWash': 'Car Wash Operations',
    'HVAC': 'HVAC Services Operations',
    'Lawn': 'Lawn & Landscape Operations',
    'Medical': 'Medical & Wellness Operations',
  };
  const buciContext = CONTEXT_MAP[prefix] || (prefix ? `${prefix} Operations` : 'Business Operations');
  return {
    cid_version: 0,
    buci_context: buciContext,
    product_type: productType || 'Not configured',
    location: location || 'Not configured',
    ceo_directives: 'Loading from CID configuration...',
    lob_directives: 'Loading from CID configuration...',
    fed_policies: 'Pending',
    state_policies: 'Pending',
    preferences: {},
  };
};

// Legacy export kept for backward compatibility — neutral placeholder
export const DEFAULT_CID_JSON = buildDefaultCidJson();

/**
 * CIDTooltip — Hover-activated overlay showing compact CID context.
 * Positioned relative to the AI Top-10 panel header.
 * Fetches from /v1/clients/{id}/top10-cid-tooltip on hover.
 * Shows within 300ms of hover initiation, dismisses within 200ms of mouse leave.
 *
 * Props:
 *   clientId  — subscriber's client_id for the API call
 *   visible   — boolean controlling tooltip visibility
 *   onDismiss — callback fired when tooltip should hide
 *   anchorRef — optional ref to the anchor element for positioning
 */
const CIDTooltip = ({ clientId, visible, onDismiss, anchorRef, productType = '', location = '' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);

  const showTimerRef = useRef(null);
  const hideTimerRef = useRef(null);
  const fetchControllerRef = useRef(null);

  // Handle show/hide transitions with timing constraints
  useEffect(() => {
    if (visible) {
      // Clear any pending hide timer
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
      // Show within 300ms of hover initiation
      showTimerRef.current = setTimeout(() => {
        setShow(true);
      }, 50); // Start transition quickly, CSS handles the 300ms animation
      setShow(true);
    } else {
      // Clear any pending show timer
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
        showTimerRef.current = null;
      }
      // Dismiss within 200ms of mouse leave
      hideTimerRef.current = setTimeout(() => {
        setShow(false);
        if (onDismiss) onDismiss();
      }, 50); // Start transition quickly, CSS handles the 200ms animation
    }

    return () => {
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, [visible, onDismiss]);

  // Fetch tooltip data when visible becomes true
  useEffect(() => {
    if (!visible || !clientId) return;

    // Abort previous request if still in flight
    if (fetchControllerRef.current) {
      fetchControllerRef.current.abort();
    }

    const controller = new AbortController();
    fetchControllerRef.current = controller;

    setLoading(true);
    setData(null);

    // Timeout after 5 seconds — treat as error and fall back to product-type-aware placeholder
    const timeoutId = setTimeout(() => {
      controller.abort();
      setData(buildDefaultCidJson(productType, location));
      setLoading(false);
    }, 5000);

    fetch(`${API_BASE}/clients/${clientId}/top10-cid-tooltip`, {
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) throw new Error('Fetch failed');
        return res.json();
      })
      .then((json) => {
        clearTimeout(timeoutId);
        if (json.status === 'unavailable') {
          setData(buildDefaultCidJson(productType, location));
        } else {
          setData(json);
        }
        setLoading(false);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          clearTimeout(timeoutId);
          setData(buildDefaultCidJson(productType, location));
          setLoading(false);
        }
      });

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [visible, clientId]);

  if (!visible && !show) return null;

  // Calculate position from anchor element
  const getPosition = () => {
    if (anchorRef && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      return { top: rect.bottom + 8, left: rect.left };
    }
    return { top: 100, left: 100 };
  };
  const pos = getPosition();

  return (
    <div
      style={{
        ...styles.overlay,
        top: pos.top,
        left: pos.left,
        opacity: visible && show ? 1 : 0,
        transform: visible && show ? 'translateY(0)' : 'translateY(-4px)',
        transition: visible
          ? 'opacity 300ms ease-out, transform 300ms ease-out'
          : 'opacity 200ms ease-in, transform 200ms ease-in',
        pointerEvents: visible ? 'auto' : 'none',
      }}
      role="tooltip"
      aria-label="Goals & Objectives Boundary"
    >
      {loading && (
        <div style={styles.loadingState}>
          <span style={styles.spinner}>⟳</span> Loading CID context...
        </div>
      )}

      {data && !loading && (
        <div style={styles.content}>
          <div style={styles.header}>⭐ Goals & Objectives Boundary</div>
          <div style={styles.fieldGrid}>
            <FieldRow label="CID Version" value={data.cid_version} />
            <FieldRow label="BUCI Context" value={data.buci_context} />
            <FieldRow label="Product Type" value={data.product_type} />
            <FieldRow label="Location" value={data.location} />
            <FieldRow label="CEO Directives" value={data.ceo_directives} />
            <FieldRow label="LOB Directives" value={data.lob_directives} />
            <FieldRow label="Fed Policies" value={data.fed_policies} />
            <FieldRow label="State Policies" value={data.state_policies} />
            {data.preferences && (
              <FieldRow
                label="Preferences"
                value={Object.entries(data.preferences)
                  .map(([k, v]) => `${k}: ${v}`)
                  .join(", ")}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/** Renders a single label–value pair in the tooltip */
const FieldRow = ({ label, value }) => (
  <div style={styles.fieldRow}>
    <span style={styles.fieldLabel}>{label}:</span>
    <span style={styles.fieldValue}>{String(value)}</span>
  </div>
);

const styles = {
  overlay: {
    position: 'fixed',
    top: 'auto',
    left: 'auto',
    zIndex: 99999,
    background: '#1a1a2e',
    color: '#e0e0e0',
    borderRadius: '10px',
    padding: '1rem',
    minWidth: '320px',
    maxWidth: '420px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
    fontSize: '0.82rem',
    lineHeight: 1.5,
  },
  loadingState: {
    padding: '0.5rem 0',
    color: '#90caf9',
    textAlign: 'center',
  },
  spinner: {
    display: 'inline-block',
    animation: 'spin 1s linear infinite',
  },
  // errorState removed — error conditions now fall back to DEFAULT_CID_JSON display
  content: {},
  header: {
    fontWeight: 700,
    fontSize: '0.9rem',
    marginBottom: '0.75rem',
    color: '#90caf9',
    borderBottom: '1px solid #333',
    paddingBottom: '0.4rem',
  },
  fieldGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem',
  },
  fieldRow: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'flex-start',
  },
  fieldLabel: {
    color: '#80cbc4',
    fontWeight: 600,
    minWidth: '110px',
    flexShrink: 0,
  },
  fieldValue: {
    color: '#e0e0e0',
    wordBreak: 'break-word',
  },
};

export default CIDTooltip;
